import React, { useEffect, useState, useCallback } from "react";
import Breadcrumbs from "../../../components/Breadcrumbs";
import { MediaProvider, useMedia } from "../../../contexts/MediaContext";
import MediaSelector from "../../../components/website/MediaSelector";
import MediaGallery from "../../../components/website/MediaGallery";
import ResizableEditArea from "../../../components/common/ResizableEditArea";
import {
  MESSAGE_TYPES,
  handleMultipleTargetUpdate,
  sendDataToIframe,
} from "../../../utils/editIframe";
import { useDragAndDrop } from "../../../hooks/useDragAndDrop";
import StatusMessage from "../../../components/common/StatusMessage";
import Iframe from "../../../components/Iframe";
import {
  clearValidationErrors,
  highlightErrorFields,
  validateFields,
  clearFieldError,
  clearSpecificFieldErrors,
  adjustArrayErrorsAfterDeletion,
  revalidateAccessPageAfterDeletion,
} from "../../../utils/formValidation";

const breadcrumbs = [
  { title: "Home", path: `/` },
  { title: "Access", path: `/access` },
];

function AccessEditPageContent() {
  const { isMediaOpen, fetchMediaFiles, selectMedia } = useMedia();
  const contentKey = "accessContent";
  const [message, setMessage] = useState("");
  const [loadingText, setLoadingText] = useState("");
  const [pageContent, setPageContent] = useState({
    page_image: {},
    page_content: "",
    studios: [
      {
        name_ja: "",
        name_en: "",
        map: "",
        information: [{ title: "", content: "" }],
        image_ids: [],
        images: [],
        display_order: 0,
      },
    ],
  });

  // ドラッグ&ドロップ並べ替え処理
  const handleStudioReorder = useCallback(
    (reorderedItems) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          studios: reorderedItems.map((studio, index) => ({
            ...studio,
            display_order: index,
          })),
        };

        clearValidationErrors();

        // iframeに全データを送信
        const iframe = document.querySelector("iframe");

        if (iframe?.contentWindow) {
          // 一時的な対策：ivy_website側の互換性のためurl/srcプロパティを追加
          const compatibleData = {
            ...newData,
            studios: newData.studios.map((studio) => ({
              ...studio,
              images:
                studio.images?.map((img) => ({
                  ...img,
                  url: img.image_url, // 互換性のため
                  src: img.image_url, // 互換性のため
                })) || [],
            })),
          };

          const message = {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: compatibleData,
            isFullUpdate: true,
            studiosCount: newData.studios.length,
            studioNames: newData.studios.map((s) => s.name_ja),
            debug: {
              studiosWithImages: newData.studios.map((s, i) => ({
                index: i,
                name: s.name_ja,
                imagesCount: s.images?.length || 0,
                firstImageUrl: s.images?.[0]?.image_url || "NO_IMAGE",
              })),
            },
          };

          iframe.contentWindow.postMessage(message, "*");
        } else {
          console.warn("iframe or contentWindow not found");
        }

        return newData;
      });
    },
    [contentKey]
  );

  // ドラッグ&ドロップフック
  const { getDraggableProps } = useDragAndDrop(
    pageContent.studios || [],
    handleStudioReorder
  );

  // データ取得処理
  const fetchPageContentData = useCallback(async (skipSetState = false) => {
    setLoadingText("読み込み中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/api/get_page_access_content/`);
      if (!response.ok) throw new Error("ネットワークレスポンスが異常です");
      const data = await response.json();
      console.log("access data", data);
      // skipSetStateがfalseの場合のみ状態を更新（初期読み込み時）
      if (!skipSetState) {
        setPageContent(data);
      }
      return data;
    } catch (error) {
      console.error("データ取得エラー:", error);
      return null;
    } finally {
      setLoadingText("");
    }
  }, []);

  useEffect(() => {
    fetchPageContentData();
    fetchMediaFiles();
  }, [fetchPageContentData, fetchMediaFiles]);

  // バリデーションルールを定義
  const validationRules = {
    page_content: { required: true },
  };

  // 更新処理
  const handlePageDataSubmit = async () => {
    clearValidationErrors();

    // 基本フィールドのバリデーション
    const errorFields = validateFields(pageContent, validationRules);

    // スタジオの検証
    if (!pageContent.studios || pageContent.studios.length === 0) {
      alert("スタジオは少なくとも1つ必要です");
      return;
    }

    // スタジオの詳細バリデーション
    pageContent.studios.forEach((studio, index) => {
      if (!studio.name_ja || studio.name_ja.trim() === "") {
        errorFields.push(`studio${index}_name_ja`);
      }
      if (!studio.map || studio.map.trim() === "") {
        errorFields.push(`studio${index}_map`);
      }

      // スタジオ情報（information）のバリデーション
      if (studio.information && studio.information.length > 0) {
        studio.information.forEach((info, infoIndex) => {
          if (!info.title || info.title.trim() === "") {
            errorFields.push(`info_title_${index}_${infoIndex}`);
          }
          if (!info.content || info.content.trim() === "") {
            errorFields.push(`info_content_${index}_${infoIndex}`);
          }
        });
      }
    });

    if (errorFields.length > 0) {
      const fieldLabels = {
        page_content: "アクセスページ本文",
      };

      // スタジオのラベルを動的に追加
      pageContent.studios.forEach((studio, index) => {
        fieldLabels[`studio${index}_name_ja`] = `スタジオ${
          index + 1
        }の名前（日本語）`;
        fieldLabels[`studio${index}_map`] = `スタジオ${index + 1}の地図`;

        // スタジオ情報のラベルを動的に追加
        if (studio.information && studio.information.length > 0) {
          studio.information.forEach((_, infoIndex) => {
            fieldLabels[`info_title_${index}_${infoIndex}`] = `スタジオ${
              index + 1
            }の情報${infoIndex + 1}のタイトル`;
            fieldLabels[`info_content_${index}_${infoIndex}`] = `スタジオ${
              index + 1
            }の情報${infoIndex + 1}の内容`;
          });
        }
      });

      highlightErrorFields(errorFields, { fieldLabels });
      setMessage(
        "入力に不備があります。赤くハイライトされた項目を確認してください。"
      );
      setTimeout(() => setMessage(""), 5000);
      return;
    }

    // 送信用データの準備
    const formData = {
      page_content: pageContent,
    };

    setLoadingText("保存中");
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(
        `${apiUrl}/api/update_page_access_content/`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || "更新に失敗しました");

      setMessage(data.message || "正常に更新されました");
      await fetchPageContentData();

      setTimeout(() => setMessage(""), 3000);
    } catch (error) {
      console.error("更新エラー:", error);
      alert(`更新に失敗しました: ${error.message}`);
    }
    setLoadingText("");
  };

  // リセット処理
  const handleReset = async () => {
    clearValidationErrors();
    setMessage("");
    // skipSetStateをtrueにして、状態更新はリセット処理内で行う
    const freshData = await fetchPageContentData(true);

    // データ取得完了後に状態を更新し、iframeに送信
    if (freshData) {
      // 新しいオブジェクト参照を作成（IframeコンポーネントのuseEffectを確実に発火させるため）
      const dataCopy = JSON.parse(JSON.stringify(freshData));

      // 一時的な対策：ivy_website側の互換性のためurl/srcプロパティを追加
      const compatibleData = {
        ...dataCopy,
        studios:
          dataCopy.studios?.map((studio) => ({
            ...studio,
            images:
              studio.images?.map((img) => ({
                ...img,
                url: img.image_url, // 互換性のため
                src: img.image_url, // 互換性のため
              })) || [],
          })) || [],
      };

      const origin = process.env.REACT_APP_ORIGIN || "http://localhost:3000";
      const iframe = document.querySelector("iframe");

      // デバッグログ：送信データを確認
      console.log("Reset: Data to send", {
        page_content: compatibleData.page_content,
        page_content_length: compatibleData.page_content?.length,
        studiosCount: compatibleData.studios?.length,
      });

      if (iframe?.contentWindow) {
        // リセットメッセージを送信
        const resetMessage = {
          type: MESSAGE_TYPES.TEXT_UPDATE,
          contentKey,
          allData: compatibleData,
          isFullUpdate: true,
          isReset: true,
        };

        console.log("Reset: Posting reset message", {
          hasAllData: !!resetMessage.allData,
          page_content: resetMessage.allData.page_content?.substring(0, 50),
          page_content_full: resetMessage.allData.page_content,
        });

        // 先にiframeにリセットメッセージを送信
        iframe.contentWindow.postMessage(resetMessage, origin);

        // リセットメッセージが確実に処理されるまで待つ
        await new Promise((resolve) => setTimeout(resolve, 100));
      } else {
        console.warn("Reset: iframe or contentWindow not found");
      }

      // その後、フォーム状態を更新
      setPageContent(compatibleData);
    }
  };

  // 入力変更処理（単一フィールド用）
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    console.log("handleInputChange called:", name, value);

    // エラークラスをクリア
    clearFieldError(e.target);

    setPageContent((prev) => {
      const newData = { ...prev, [name]: value };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      console.log("iframe found:", !!iframe);
      console.log("iframe contentWindow:", !!iframe?.contentWindow);

      if (iframe?.contentWindow) {
        const targetClass = `.edit_${name}`;
        const messageType =
          e.type === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.TEXT_UPDATE;

        console.log("Sending message:", {
          type: messageType,
          target: targetClass,
          contentKey,
          fieldName: name,
          value,
          origin: process.env.REACT_APP_ORIGIN,
        });

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: targetClass,
            contentKey,
            fieldName: name,
            value,
          },
          "*"
        );
      }

      return newData;
    });
  };

  // メディア選択処理
  const handleMediaSelect = (fieldName, media) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        [fieldName]: media,
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_${fieldName}`;

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.IMAGE_UPDATE,
            target: targetClass,
            contentKey,
            fieldName,
            value: media,
          },
          "*"
        );
      }

      return newData;
    });
  };

  // スタジオ入力変更処理
  const handleStudioInputChange = (e, index, fieldName) => {
    const { value } = e.target;
    const eventType = e.type;

    // エラークラスをクリア
    clearFieldError(e.target);

    setPageContent((prev) => {
      const newData = {
        ...prev,
        studios: prev.studios.map((studio, i) =>
          i === index ? { ...studio, [fieldName]: value } : studio
        ),
      };

      // name_jaフィールドの場合は複数ターゲット更新
      if (fieldName === "name_ja") {
        const targets = [
          `.edit_studio_${index}_name_ja_menu`,
          `.edit_studio_${index}_name_ja_title`,
          `.edit_studio_${index}_name_ja_access`,
        ];

        const messageType =
          eventType === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.ARRAY_ITEM_UPDATE;

        handleMultipleTargetUpdate(value, targets, {
          contentKey,
          messageType,
          arrayFieldName: "studios",
          index,
          itemFieldName: fieldName,
        });
      } else {
        // 通常の単一ターゲット更新
        const iframe = document.querySelector("iframe");
        if (iframe?.contentWindow) {
          const targetClass = `.edit_studio_${index}_${fieldName}`;
          const messageType =
            eventType === "focus"
              ? MESSAGE_TYPES.SCROLL_TO
              : MESSAGE_TYPES.ARRAY_ITEM_UPDATE;

          iframe.contentWindow.postMessage(
            {
              type: messageType,
              target: targetClass,
              contentKey,
              fieldName,
              value,
              index,
              itemFieldName: fieldName,
              arrayFieldName: "studios",
            },
            "*"
          );
        }
      }

      return newData;
    });
  };

  // スタジオ画像選択処理（複数選択対応）
  const handleStudioImageSelect = (studioIndex, selectedImages) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        studios: prev.studios.map((studio, i) => {
          if (i === studioIndex) {
            return {
              ...studio,
              images: selectedImages,
              image_ids: selectedImages.map((img) => img.id),
            };
          }
          return studio;
        }),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_studio_${studioIndex}_images`;

        console.log("Sending studio images update:", {
          type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
          target: targetClass,
          studioIndex,
          imagesCount: selectedImages.length,
          imageUrls: selectedImages.map((img) => img.image_url),
          imageDetails: selectedImages.map((img) => ({
            id: img.id,
            url: img.image_url,
            alt: img.alt_text,
            isValidUrl: !!img.image_url && img.image_url.trim() !== "",
          })),
        });

        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.MULTIPLE_IMAGES_UPDATE,
            target: targetClass,
            contentKey,
            fieldName: "images",
            index: studioIndex,
            value: selectedImages, // image_urlを使用
            // 個別画像更新時はallDataを送信しない
            isFullUpdate: false,
          },
          "*"
        );
      }

      return newData;
    });
  };

  // スタジオ情報追加（+Informationボタン用）
  const handleAddInformation = useCallback(
    (studioIndex) => {
      setPageContent((prev) => {
        const newData = {
          ...prev,
          studios: prev.studios.map((studio, i) => {
            if (i === studioIndex) {
              return {
                ...studio,
                information: [
                  ...(studio.information || []),
                  { title: "", content: "" },
                ],
              };
            }
            return studio;
          }),
        };
        return newData;
      });
    },
    [contentKey]
  );

  // スタジオ情報削除
  const handleDeleteInformation = (studioIndex, infoIndex) => {
    setPageContent((prev) => {
      const newData = {
        ...prev,
        studios: prev.studios.map((studio, i) => {
          if (i === studioIndex) {
            return {
              ...studio,
              information: studio.information.filter((_, j) => j !== infoIndex),
            };
          }
          return studio;
        }),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        iframe.contentWindow.postMessage(
          {
            type: MESSAGE_TYPES.TEXT_UPDATE,
            contentKey,
            allData: newData,
          },
          "*"
        );
      }

      // 削除後に対象スタジオの情報を再バリデーション
      setTimeout(() => {
        revalidateAccessPageAfterDeletion(newData, studioIndex);
      }, 100);

      return newData;
    });
  };

  // スタジオ情報入力変更
  const handleInformationInputChange = (e, studioIndex, infoIndex, field) => {
    const value = e.target.value;
    const eventType = e.type;

    setPageContent((prev) => {
      const newData = {
        ...prev,
        studios: prev.studios.map((studio, i) => {
          if (i === studioIndex) {
            return {
              ...studio,
              information: studio.information.map((info, j) =>
                j === infoIndex ? { ...info, [field]: value } : info
              ),
            };
          }
          return studio;
        }),
      };

      // iframeにメッセージ送信
      const iframe = document.querySelector("iframe");
      if (iframe?.contentWindow) {
        const targetClass = `.edit_studio_${studioIndex}_info_${infoIndex}_${field}`;
        const messageType =
          eventType === "focus"
            ? MESSAGE_TYPES.SCROLL_TO
            : MESSAGE_TYPES.ARRAY_ITEM_UPDATE;

        console.log("Sending information input message:", {
          type: messageType,
          target: targetClass,
          value,
          studioIndex,
          infoIndex,
          field,
        });

        iframe.contentWindow.postMessage(
          {
            type: messageType,
            target: targetClass,
            contentKey,
            fieldName: field,
            index: studioIndex,
            value,
            itemFieldName: field,
            arrayFieldName: "information",
          },
          "*"
        );
      }

      return newData;
    });
  };

  // スタジオ追加（QaEditPageスタイル）
  const handleAddStudio = (insertAfterIndex = null) => {
    setPageContent((prev) => {
      const newStudio = {
        name_ja: "",
        name_en: "",
        map: "",
        information: [{ title: "", content: "" }],
        image_ids: [],
        images: [],
        display_order: 0,
      };

      let newStudios;
      if (insertAfterIndex !== null) {
        // 指定位置に挿入
        newStudios = [
          ...prev.studios.slice(0, insertAfterIndex + 1),
          newStudio,
          ...prev.studios.slice(insertAfterIndex + 1),
        ];
      } else {
        // 末尾に追加
        newStudios = [...prev.studios, newStudio];
      }

      // display_orderをインデックスに基づいて更新
      newStudios = newStudios.map((studio, index) => ({
        ...studio,
        display_order: index,
      }));

      const newData = {
        ...prev,
        studios: newStudios,
      };

      // iframeへのメッセージ送信は削除（IframeコンポーネントのuseEffectに任せる）
      // これにより重複送信を防ぐ

      return newData;
    });
  };

  // スタジオ削除（QaEditPageスタイル）
  const handleDeleteStudio = (studioIndex) => {
    if (pageContent.studios.length <= 1) {
      alert("スタジオは最低1つ必要です");
      return;
    }

    if (!window.confirm("このスタジオを削除してもよろしいですか？")) {
      return;
    }

    const currentStudiosLength = pageContent.studios.length;

    setPageContent((prev) => {
      // スタジオを削除
      let newStudios = prev.studios.filter((_, i) => i !== studioIndex);

      // display_orderをインデックスに基づいて更新
      newStudios = newStudios.map((studio, index) => ({
        ...studio,
        display_order: index,
      }));

      const newData = {
        ...prev,
        studios: newStudios,
      };

      // iframeへのメッセージ送信は削除（IframeコンポーネントのuseEffectに任せる）
      // これにより重複送信を防ぐ

      return newData;
    });

    // スタジオ削除時のエラー状態調整
    setTimeout(() => {
      adjustArrayErrorsAfterDeletion(
        "studio",
        studioIndex,
        currentStudiosLength - 1
      );
    }, 0);
  };

  // ファイルドロップ処理
  const handleDrop = useCallback(
    async (e) => {
      if (e.preventDefault) {
        e.preventDefault();
      }
      setLoadingText("アップロード中");

      // ファイルを取得（input change event or drag & drop event）
      const file = e.target?.files?.[0] || e.dataTransfer?.files?.[0];

      if (!file) {
        console.warn("No file found in event");
        setLoadingText("");
        return;
      }

      try {
        const formData = new FormData();
        formData.append("images", file);
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(`${apiUrl}/api/image_upload/`, {
          method: "POST",
          credentials: "include",
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.message);

        await fetchMediaFiles();
        if (data.images?.[0]) {
          selectMedia(data.images[0]);
        }
      } catch (error) {
        console.error("アップロードエラー:", error);
        alert("アップロードに失敗しました");
      }
      setLoadingText("");
    },
    [fetchMediaFiles, selectMedia]
  );

  return (
    <>
      <ResizableEditArea>
        {({ onResizeStart }) => (
          <>
            <Breadcrumbs
              breadcrumbs={breadcrumbs}
              onResizeStart={onResizeStart}
            />
            <StatusMessage message={message} type="success" />

            <div className="form">
              {/* ページ上部 */}
              <section>
                <div className="area3">
                  <div className="item">
                    <div className="forms">
                      <textarea
                        className="input2"
                        name="page_content"
                        value={pageContent.page_content}
                        onChange={handleInputChange}
                        onFocus={handleInputChange}
                        placeholder="ページ本文"
                        data-label="ページ本文"
                        data-required="true"
                      />
                      <div className="input3 img-form">
                        <MediaSelector
                          label="ページ画像"
                          aspectRatio="9:16"
                          type="image"
                          fieldName="page_image"
                          selectedMedia={pageContent.page_image}
                          onSelect={(media) =>
                            handleMediaSelect("page_image", media)
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              {/* スタジオ一覧 */}
              <section>
                <div className="area4">
                  <div className="item">
                    <div className="nest-item-wrap">
                      {pageContent.studios?.map((studio, index) => (
                        <div
                          className="nest-item"
                          key={index}
                          {...getDraggableProps(index)}
                        >
                          <div className="left ctrl">
                            <p>{index + 1}</p>
                            <div className="icon-wrap">
                              <i
                                className="icon delete"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleDeleteStudio(index)}
                              ></i>
                              <i
                                className="icon add"
                                style={{ cursor: "pointer" }}
                                onClick={() => handleAddStudio(index)}
                              ></i>
                            </div>
                          </div>
                          <div className="right">
                            <input
                              type="text"
                              name={`studio${index}_name_ja`}
                              value={studio.name_ja}
                              onChange={(e) =>
                                handleStudioInputChange(e, index, "name_ja")
                              }
                              onFocus={(e) =>
                                handleStudioInputChange(e, index, "name_ja")
                              }
                              placeholder="スタジオ名（日本語）"
                              data-required="true"
                            />
                            {/* <input
                              type="text"
                              name={`studio${index}_name_en`}
                              value={studio.name_en}
                              onChange={(e) =>
                                handleStudioInputChange(e, index, "name_en")
                              }
                              onFocus={(e) =>
                                handleStudioInputChange(e, index, "name_en")
                              }
                              placeholder="スタジオ名（英語）"
                              data-required="true"
                            />*/}
                            <input
                              type="text"
                              name={`studio${index}_map`}
                              value={studio.map}
                              onChange={(e) =>
                                handleStudioInputChange(e, index, "map")
                              }
                              onFocus={(e) =>
                                handleStudioInputChange(e, index, "map")
                              }
                              placeholder="地図情報"
                              data-required="true"
                            />

                            {/* スタジオ情報（動的） */}
                            <dl className="information txt-form">
                              {(studio.information || []).map(
                                (infoItem, infoIndex) => (
                                  <React.Fragment key={infoIndex}>
                                    <dt>
                                      <input
                                        type="text"
                                        id={`info_title_${index}_${infoIndex}`}
                                        name={`info_title_${index}_${infoIndex}`}
                                        className="q"
                                        value={infoItem.title || ""}
                                        onChange={(e) =>
                                          handleInformationInputChange(
                                            e,
                                            index,
                                            infoIndex,
                                            "title"
                                          )
                                        }
                                        onFocus={(e) =>
                                          handleInformationInputChange(
                                            e,
                                            index,
                                            infoIndex,
                                            "title"
                                          )
                                        }
                                        placeholder="項目名を入力"
                                        data-required="true"
                                      />
                                    </dt>
                                    <dd
                                      onClick={(e) => {
                                        // input要素のクリックでは削除しない
                                        if (e.target.tagName !== "INPUT") {
                                          handleDeleteInformation(
                                            index,
                                            infoIndex
                                          );
                                        }
                                      }}
                                      style={{ cursor: "pointer" }}
                                    >
                                      <input
                                        type="text"
                                        id={`info_content_${index}_${infoIndex}`}
                                        name={`info_content_${index}_${infoIndex}`}
                                        className="q"
                                        value={infoItem.content || ""}
                                        onChange={(e) =>
                                          handleInformationInputChange(
                                            e,
                                            index,
                                            infoIndex,
                                            "content"
                                          )
                                        }
                                        onFocus={(e) =>
                                          handleInformationInputChange(
                                            e,
                                            index,
                                            infoIndex,
                                            "content"
                                          )
                                        }
                                        placeholder="内容を入力"
                                        data-required="true"
                                      />
                                    </dd>
                                  </React.Fragment>
                                )
                              )}
                            </dl>

                            <div className="accent-wrap">
                              <p
                                onClick={() => handleAddInformation(index)}
                                style={{ cursor: "pointer" }}
                              >
                                <i className="icon add"></i>
                                <span>Information</span>
                              </p>
                            </div>

                            <div className="input3 img-form">
                              <MediaSelector
                                label="スタジオ画像"
                                aspectRatio="16:9"
                                type="image"
                                fieldName={`studio_${index}_images`}
                                selectedMedia={studio.images || []}
                                onSelect={(images) =>
                                  handleStudioImageSelect(index, images)
                                }
                                mode="multiple"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </section>
            </div>

            <div className="submit-area">
              <button type="button" onClick={handleReset}>
                Reset
              </button>
              <button type="button" onClick={handlePageDataSubmit}>
                Submit
              </button>
            </div>

            {isMediaOpen && <MediaGallery onUpload={handleDrop} />}
          </>
        )}
      </ResizableEditArea>

      {loadingText && (
        <div className="overlay">
          <div className="loading">
            <div className="circle"></div>
            <p>{loadingText}...</p>
          </div>
        </div>
      )}

      <Iframe path="/access" data={pageContent} contentKey={contentKey} />
    </>
  );
}

export default function AccessEditPage() {
  return (
    <MediaProvider>
      <AccessEditPageContent />
    </MediaProvider>
  );
}
